import React, { useContext, useState } from 'react';
import Styled, { ThemeContext } from 'styled-components';
import { gql, useQuery } from '@apollo/client';
import Media from 'react-media';
import { Trans } from '@lingui/react';
import select from '@components/form/Select';
import Languages from '@organisms/Languages';
import { Container, Box, Flex } from '@components/layout/Grid';
import Icon from '@components/Icon';
import { Body, Header, Modal } from '@organisms/modals/base';
import { Instructor, Query } from '@gql/generated';
import { LIVE_KINDS } from '@lib/constants';

const Filters = Styled.div`
  position: sticky;
  top: 0;
  padding: 10px 20px;
  background-color: #F0F5FB;
  box-shadow: 2px 2px 4px #09080838;
  z-index: 9;
`;

const Select = Styled(select)`
  background-color: white;

  .MuiInputBase-root {
    .MuiSelect-select {
      font-size: 13px;
      border: solid 1px #dddddd;
      border-radius: 5px;
      padding: 10px 35px 10px 14px;
    }

    .MuiSelect-icon {
      right: 5px;
    }
  }
`;

const MobileFilterOpen = Styled.button`
  border: 1px solid lightgrey;
  border-radius: 3px;
  padding: 12px 20px;
  margin: 0 auto;
  background-color: white;
`;

const INSTRUCTORS_QUERY = gql`
  query instructors {
    instructors(option: live_events_instructors_only, perPage: 100, page: 1) {
      data {
        name
        slug
      }
    }
  }
`;

const sortOptions = [
  {
    label: <Trans id="live.listings_page.filters.sort_label" />,
    value: '-1',
  },
  {
    label: <Trans id="live.listings_page.filters.sort_by_date" />,
    value: 'first_event_date',
  },
  {
    label: <Trans id="live.listings_page.filters.sort_by_price" />,
    value: 'amount_cents',
  },
];

const FilterInputs = ({
  isMobile,
  languages = ['se', 'en'],
  instructor,
  sortBy,
  kind,
  kinds,
  onLanguageChange,
  onInstructorChange,
  onSortChange,
  onKindChange,
}) => {
  const { data: instructorsData, error: instructorsError } = useQuery<{
    instructors: Query['instructors'];
  }>(INSTRUCTORS_QUERY);

  const mt = isMobile ? 20 : 0;
  const direction = isMobile ? 'column' : 'row';

  return (
    <>
      <Flex flexDirection={direction}>
        <Box mr={10} mt={mt}>
          <Select
            name="sort"
            options={sortOptions}
            valueKey="value"
            displayTextKey="label"
            selectedValue={sortBy === null ? '-1' : sortBy}
            onChange={e => {
              const value = e.target.value;
              onSortChange(value === '-1' ? null : value);
            }}
          />
        </Box>
        {!instructorsError ? (
          <Box mr={10} mt={mt}>
            <Select
              name="instructors"
              options={[
                {
                  label: <Trans id="live.listings_page.filters.instructors" />,
                  value: '-1',
                },
                ...(instructorsData?.instructors.data || []).map(
                  instructor => ({
                    label: instructor.name,
                    value: instructor.slug,
                  })
                ),
              ]}
              valueKey="value"
              displayTextKey="label"
              selectedValue={instructor === null ? '-1' : instructor}
              onChange={e => {
                const instructor = (e.target as HTMLSelectElement).value;
                onInstructorChange(instructor === '-1' ? null : instructor);
              }}
            />
          </Box>
        ) : null}
        <Box mr={10} mt={mt}>
          <Select
            name="kind"
            options={[
              {
                label: <Trans id="live.listings_page.filters.kind_label" />,
                value: '-1',
              },
              ...(kinds || [])?.map(kind => ({
                label: LIVE_KINDS[kind],
                value: kind,
              })),
            ]}
            valueKey="value"
            displayTextKey="label"
            selectedValue={kind === null ? '-1' : kind}
            onChange={e => {
              const value = e.target.value;
              onKindChange(value === '-1' ? null : value);
            }}
          />
        </Box>
      </Flex>
      <Flex
        mt={mt}
        flex={1}
        justifyContent={isMobile ? 'center' : 'flex-end'}
        alignItems="center"
      >
        <span>
          <Trans id="live.listings_page.filters.languages" />:
        </span>
        <Languages languages={languages} onChange={onLanguageChange} />
      </Flex>
    </>
  );
};

const FilterModal = ({
  languages,
  instructor,
  sortBy,
  kind,
  kinds,
  onLanguageChange,
  onInstructorChange,
  onSortChange,
  onKindChange,
  onClose,
}) => {
  return (
    <Modal open={true}>
      <Header handleClose={onClose}>
        <Trans id="live.listings_page.filters.sort_modal_title" />
      </Header>
      <Body>
        <Flex flexDirection="column-reverse">
          <FilterInputs
            languages={languages}
            instructor={instructor}
            sortBy={sortBy}
            kind={kind}
            kinds={kinds}
            onInstructorChange={onInstructorChange}
            onLanguageChange={onLanguageChange}
            onSortChange={onSortChange}
            onKindChange={onKindChange}
            isMobile
          />
        </Flex>
      </Body>
    </Modal>
  );
};

type Props = {
  className?: string;
  languages: string[];
  instructor: string;
  sortBy: string;
  kinds: string[];
  kind: string;
  onLanguageChange(language: string): void;
  onInstructorChange(instructor: string): void;
  onSortChange(sort: string): void;
  onKindChange(kind: string): void;
};

const EventsFilters = (props: Props) => {
  const {
    className = '',
    languages,
    instructor,
    sortBy,
    kinds,
    kind,
    onLanguageChange,
    onInstructorChange,
    onSortChange,
    onKindChange,
  } = props;
  const theme = useContext(ThemeContext);
  const [isModalOpen, openModal] = useState(false);

  return (
    <Filters className={className} data-testid="EventsFilter">
      <Container flexWrap="wrap">
        <Media query={`(min-width: ${theme.viewports.tablet}px)`}>
          {matches => {
            if (matches) {
              return (
                <FilterInputs
                  languages={languages}
                  instructor={instructor}
                  sortBy={sortBy}
                  kind={kind}
                  kinds={kinds}
                  onInstructorChange={onInstructorChange}
                  onLanguageChange={onLanguageChange}
                  onSortChange={onSortChange}
                  onKindChange={onKindChange}
                  isMobile={false}
                />
              );
            } else {
              return (
                <>
                  <MobileFilterOpen
                    onClick={() => {
                      openModal(true);
                    }}
                  >
                    <Icon name="ygb-icon-restart" />{' '}
                    <Trans id="live.listings_page.filters.sort_mobile_label" />
                  </MobileFilterOpen>
                  {isModalOpen ? (
                    <FilterModal
                      languages={languages}
                      instructor={instructor}
                      sortBy={sortBy}
                      kind={kind}
                      kinds={kinds}
                      onInstructorChange={onInstructorChange}
                      onLanguageChange={onLanguageChange}
                      onSortChange={onSortChange}
                      onKindChange={onKindChange}
                      onClose={() => openModal(false)}
                    />
                  ) : null}
                </>
              );
            }
          }}
        </Media>
      </Container>
    </Filters>
  );
};

export default EventsFilters;
